/* ==========================================================================
   Spacewallart Parfums — theme.js
   ========================================================================== */
(function () {
  'use strict';

  const qs = (sel, ctx) => (ctx || document).querySelector(sel);
  const qsa = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  /* ---------- Focus trap helper ---------- */
  const FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  /* ==========================================================
     Drawers / overlays
     ========================================================== */
  const Drawers = {
    active: null,
    lastFocus: null,

    init() {
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-drawer-open]');
        if (opener) {
          e.preventDefault();
          this.open(opener.getAttribute('data-drawer-open'));
          return;
        }
        const closer = e.target.closest('[data-drawer-close]');
        if (closer) {
          e.preventDefault();
          this.close();
        }
      });

      const overlay = qs('[data-overlay]');
      if (overlay) overlay.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.active) this.close();
        if (e.key === 'Tab' && this.active) this.trap(e);
      });
    },

    open(id) {
      const el = qs('[data-drawer="' + id + '"]');
      if (!el) return;
      if (this.active && this.active !== el) this.close(true);
      this.lastFocus = document.activeElement;
      this.active = el;
      el.setAttribute('data-open', 'true');
      el.removeAttribute('aria-hidden');
      const overlay = qs('[data-overlay]');
      if (overlay) overlay.setAttribute('data-open', 'true');
      document.body.classList.add('is-locked');
      window.setTimeout(() => {
        const focusTarget = el.querySelector('[data-drawer-focus]') || el.querySelector(FOCUSABLE);
        if (focusTarget) focusTarget.focus();
      }, 120);
      document.dispatchEvent(new CustomEvent('drawer:open', { detail: { id: id } }));
    },

    close(keepOverlay) {
      if (!this.active) return;
      this.active.setAttribute('data-open', 'false');
      this.active.setAttribute('aria-hidden', 'true');
      this.active = null;
      if (!keepOverlay) {
        const overlay = qs('[data-overlay]');
        if (overlay) overlay.setAttribute('data-open', 'false');
        document.body.classList.remove('is-locked');
      }
      if (this.lastFocus && !keepOverlay) {
        try { this.lastFocus.focus(); } catch (err) { /* noop */ }
      }
    },

    trap(e) {
      const items = qsa(FOCUSABLE, this.active).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  /* ==========================================================
     Toast
     ========================================================== */
  let toastTimer;
  window.showToast = function (message) {
    const toast = qs('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.setAttribute('data-open', 'true');
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.setAttribute('data-open', 'false'), 3600);
  };

  /* ==========================================================
     Accordions
     ========================================================== */
  function initAccordions(scope) {
    qsa('[data-accordion-trigger]', scope).forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => {
        const panel = btn.nextElementSibling;
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        const group = btn.closest('[data-accordion-single]');
        if (group && !isOpen) {
          qsa('[data-accordion-trigger]', group).forEach((other) => {
            if (other === btn) return;
            other.setAttribute('aria-expanded', 'false');
            if (other.nextElementSibling) other.nextElementSibling.setAttribute('data-open', 'false');
          });
        }
        btn.setAttribute('aria-expanded', String(!isOpen));
        if (panel) panel.setAttribute('data-open', String(!isOpen));
      });
    });
  }

  /* ==========================================================
     Carousels
     ========================================================== */
  function initCarousels(scope) {
    qsa('[data-carousel]', scope).forEach((root) => {
      if (root.dataset.bound) return;
      root.dataset.bound = '1';
      const track = qs('[data-carousel-track]', root);
      const prev = qs('[data-carousel-prev]', root);
      const next = qs('[data-carousel-next]', root);
      if (!track) return;

      const step = () => {
        const first = track.firstElementChild;
        if (!first) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
        return first.getBoundingClientRect().width + gap;
      };

      const update = () => {
        if (!prev || !next) return;
        const max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
        const nav = qs('[data-carousel-nav]', root);
        if (nav) nav.hidden = track.scrollWidth <= track.clientWidth + 4;
      };

      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ==========================================================
     Scroll reveal
     ========================================================== */
  function initReveal(scope) {
    const items = qsa('[data-reveal]', scope).filter((el) => !el.dataset.revealBound);
    if (!items.length) return;
    if (!window.theme || window.theme.animations === false || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    items.forEach((el) => {
      el.dataset.revealBound = '1';
      io.observe(el);
    });
  }

  /* ==========================================================
     Header behaviour
     ========================================================== */
  function initHeader() {
    const header = qs('[data-header]');
    if (!header) return;

    const setHeight = () => {
      document.documentElement.style.setProperty('--header-h', header.offsetHeight + 'px');
    };
    setHeight();
    window.addEventListener('resize', setHeight);

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        header.classList.toggle('is-scrolled', window.scrollY > 12);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    /* mobile submenu toggles */
    qsa('[data-mobile-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        const sub = btn.nextElementSibling;
        if (sub) sub.hidden = open;
      });
    });
  }

  /* ==========================================================
     Announcement rotator
     ========================================================== */
  function initAnnounce() {
    qsa('[data-announce-rotator]').forEach((root) => {
      const slides = qsa('[data-announce-slide]', root);
      if (slides.length < 2) return;
      const delay = parseInt(root.dataset.announceRotator, 10) || 5000;
      let i = 0;
      window.setInterval(() => {
        slides[i].setAttribute('data-active', 'false');
        i = (i + 1) % slides.length;
        slides[i].setAttribute('data-active', 'true');
      }, delay);
    });
  }

  /* ==========================================================
     Quantity steppers
     ========================================================== */
  function initQuantity(scope) {
    qsa('[data-qty]', scope).forEach((root) => {
      if (root.dataset.bound) return;
      root.dataset.bound = '1';
      const input = qs('input', root);
      if (!input) return;
      qsa('[data-qty-step]', root).forEach((btn) => {
        btn.addEventListener('click', () => {
          const delta = parseInt(btn.dataset.qtyStep, 10);
          const min = parseInt(input.min, 10) || 0;
          const max = input.max ? parseInt(input.max, 10) : Infinity;
          let val = (parseInt(input.value, 10) || 0) + delta;
          val = Math.min(Math.max(val, min), max);
          if (String(val) === input.value) return;
          input.value = val;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ==========================================================
     Product gallery
     ========================================================== */
  function initGallery(scope) {
    qsa('[data-gallery]', scope).forEach((root) => {
      if (root.dataset.bound) return;
      root.dataset.bound = '1';
      const viewport = qs('[data-gallery-viewport]', root);
      const thumbs = qsa('[data-gallery-thumb]', root);
      const dots = qsa('[data-gallery-dot]', root);
      if (!viewport) return;
      const slides = qsa('[data-gallery-slide]', viewport);

      const goTo = (index) => {
        const slide = slides[index];
        if (!slide) return;
        viewport.scrollTo({ left: slide.offsetLeft - viewport.offsetLeft, behavior: 'smooth' });
      };

      const setActive = (index) => {
        thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === index)));
        dots.forEach((d, i) => d.setAttribute('aria-current', String(i === index)));
      };

      thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => { goTo(i); setActive(i); });
      });
      dots.forEach((dot, i) => dot.addEventListener('click', () => { goTo(i); setActive(i); }));

      let raf;
      viewport.addEventListener('scroll', () => {
        window.cancelAnimationFrame(raf);
        raf = window.requestAnimationFrame(() => {
          const idx = Math.round(viewport.scrollLeft / viewport.clientWidth);
          setActive(Math.min(idx, slides.length - 1));
        });
      }, { passive: true });

      /* jump to the media matching a selected variant */
      root.addEventListener('gallery:select', (e) => {
        const mediaId = e.detail && e.detail.mediaId;
        if (!mediaId) return;
        const index = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
        if (index > -1) { goTo(index); setActive(index); }
      });

      setActive(0);
    });
  }

  /* ==========================================================
     Variant picker
     ========================================================== */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.url = this.dataset.url;
      this.variants = this.parseVariants();
      this.addEventListener('change', this.onChange.bind(this));
    }

    parseVariants() {
      const script = this.querySelector('[data-variant-json]');
      if (!script) return [];
      try { return JSON.parse(script.textContent); } catch (e) { return []; }
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked')).map((i) => i.value);
    }

    onChange() {
      const opts = this.selectedOptions;
      const variant = this.variants.find((v) =>
        v.options.every((opt, i) => opt === opts[i])
      );
      this.updateAvailability(opts);
      if (!variant) {
        this.setUnavailable();
        return;
      }
      this.current = variant;
      this.updateUrl(variant);
      this.updateFormId(variant);
      this.updatePrice(variant);
      this.updateAvailabilityUi(variant);
      this.updateMedia(variant);
      this.updateLabels();
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: variant, sectionId: this.sectionId } }));
    }

    updateLabels() {
      this.querySelectorAll('[data-option-index]').forEach((group) => {
        const idx = parseInt(group.dataset.optionIndex, 10);
        const target = group.querySelector('[data-option-value]');
        if (target) target.textContent = this.selectedOptions[idx] || '';
      });
    }

    /* Grey out combinations that do not exist */
    updateAvailability(opts) {
      this.querySelectorAll('[data-option-index]').forEach((group) => {
        const idx = parseInt(group.dataset.optionIndex, 10);
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const trial = opts.slice();
          trial[idx] = input.value;
          const match = this.variants.find((v) =>
            v.options.every((opt, i) => (i === idx ? opt === input.value : opt === trial[i]))
          );
          const available = !!(match && match.available);
          input.disabled = !match;
          input.closest('.variant-option').classList.toggle('is-unavailable', !available);
        });
      });
    }

    setUnavailable() {
      const btn = document.getElementById('AddToCart-' + this.sectionId);
      if (btn) {
        btn.setAttribute('disabled', 'disabled');
        const label = btn.querySelector('.btn__label');
        if (label) label.textContent = window.theme.strings.unavailable;
      }
    }

    updateUrl(variant) {
      if (!variant || !this.url) return;
      window.history.replaceState({}, '', this.url + '?variant=' + variant.id);
    }

    updateFormId(variant) {
      const input = document.getElementById('VariantId-' + this.sectionId);
      if (input) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updatePrice(variant) {
      const holder = document.getElementById('Price-' + this.sectionId);
      if (!holder) return;
      const current = holder.querySelector('[data-price-current]');
      const was = holder.querySelector('[data-price-was]');
      const save = holder.querySelector('[data-price-save]');
      if (current) current.textContent = window.formatMoney(variant.price);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      holder.classList.toggle('price--onsale', !!onSale);
      if (was) {
        was.hidden = !onSale;
        if (onSale) was.textContent = window.formatMoney(variant.compare_at_price);
      }
      if (save) {
        save.hidden = !onSale;
        if (onSale) {
          const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          save.textContent = 'Save ' + pct + '%';
        }
      }
      const unit = holder.querySelector('[data-price-unit]');
      if (unit) unit.hidden = true;

      /* mirror into the sticky bar */
      const sticky = document.querySelector('[data-sticky-price]');
      if (sticky) sticky.textContent = window.formatMoney(variant.price);
    }

    updateAvailabilityUi(variant) {
      const btn = document.getElementById('AddToCart-' + this.sectionId);
      const stickyBtn = document.querySelector('[data-sticky-atc-button]');
      [btn, stickyBtn].forEach((el) => {
        if (!el) return;
        const label = el.querySelector('.btn__label');
        if (variant.available) {
          el.removeAttribute('disabled');
          if (label) label.textContent = window.theme.strings.addToCart;
        } else {
          el.setAttribute('disabled', 'disabled');
          if (label) label.textContent = window.theme.strings.soldOut;
        }
      });
      const stock = document.getElementById('Stock-' + this.sectionId);
      if (stock) stock.hidden = !variant.available;
      const stockOut = document.getElementById('StockOut-' + this.sectionId);
      if (stockOut) stockOut.hidden = !!variant.available;
      const sku = document.getElementById('Sku-' + this.sectionId);
      if (sku) {
        sku.textContent = variant.sku || '';
        sku.hidden = !variant.sku;
      }
    }

    updateMedia(variant) {
      if (!variant.featured_media) return;
      const gallery = document.querySelector('[data-gallery]');
      if (gallery) {
        gallery.dispatchEvent(new CustomEvent('gallery:select', { detail: { mediaId: variant.featured_media.id } }));
      }
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ==========================================================
     Money formatting
     ========================================================== */
  window.formatMoney = function (cents, format) {
    const fmt = format || (window.theme && window.theme.moneyFormat) || '${{amount}}';
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function decimals(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cts = parts[1] ? decimal + parts[1] : '';
      return dollars + cts;
    }

    const match = fmt.match(placeholder);
    switch (match && match[1]) {
      case 'amount': value = decimals(cents, 2); break;
      case 'amount_no_decimals': value = decimals(cents, 0); break;
      case 'amount_with_comma_separator': value = decimals(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = decimals(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = decimals(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = decimals(cents, 0, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = decimals(cents, 2, ' ', '.'); break;
      default: value = decimals(cents, 2);
    }
    return fmt.replace(placeholder, value);
  };

  /* ==========================================================
     Sticky add to cart
     ========================================================== */
  function initStickyAtc() {
    const bar = qs('[data-sticky-atc]');
    const anchor = qs('[data-sticky-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          bar.setAttribute('data-visible', String(!entry.isIntersecting && entry.boundingClientRect.top < 0));
        });
      },
      { threshold: 0 }
    );
    io.observe(anchor);

    const stickyBtn = qs('[data-sticky-atc-button]', bar);
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        const form = qs('[data-product-form]');
        if (form) form.requestSubmit ? form.requestSubmit() : form.querySelector('[type="submit"]').click();
      });
    }
  }

  /* ==========================================================
     Predictive search
     ========================================================== */
  function initSearch() {
    const input = qs('[data-search-input]');
    const results = qs('[data-search-results]');
    const defaults = qs('[data-search-default]');
    if (!input || !results) return;
    let controller;
    let timer;

    input.addEventListener('input', () => {
      const term = input.value.trim();
      window.clearTimeout(timer);
      if (term.length < 2) {
        results.innerHTML = '';
        if (defaults) defaults.hidden = false;
        return;
      }
      timer = window.setTimeout(() => {
        if (controller) controller.abort();
        controller = new AbortController();
        const url =
          window.theme.routes.predictiveSearch +
          '?q=' + encodeURIComponent(term) +
          '&resources[type]=product,page,article&resources[limit]=6&section_id=predictive-search';
        fetch(url, { signal: controller.signal })
          .then((r) => r.text())
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const inner = doc.querySelector('[data-predictive-inner]');
            results.innerHTML = inner ? inner.innerHTML : '';
            if (defaults) defaults.hidden = true;
          })
          .catch(() => {});
      }, 260);
    });
  }

  /* ==========================================================
     Tabs
     ========================================================== */
  function initTabs(scope) {
    qsa('[data-tabs]', scope).forEach((root) => {
      if (root.dataset.bound) return;
      root.dataset.bound = '1';
      const tabs = qsa('[data-tab]', root);
      const panels = qsa('[data-tab-panel]', root);
      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const id = tab.dataset.tab;
          tabs.forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
          panels.forEach((p) => { p.hidden = p.dataset.tabPanel !== id; });
          initCarousels(root);
        });
      });
    });
  }

  /* ==========================================================
     Boot
     ========================================================== */
  function boot(scope) {
    initAccordions(scope);
    initCarousels(scope);
    initReveal(scope);
    initQuantity(scope);
    initGallery(scope);
    initTabs(scope);
  }

  function init() {
    Drawers.init();
    initHeader();
    initAnnounce();
    initSearch();
    initStickyAtc();
    boot(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor support */
  document.addEventListener('shopify:section:load', (e) => {
    boot(e.target);
    initHeader();
    initAnnounce();
    initStickyAtc();
    initSearch();
  });
  document.addEventListener('shopify:section:select', (e) => boot(e.target));

  window.ThemeDrawers = Drawers;
  window.themeBoot = boot;
})();
